/**
 * Cart Reward Progress System
 * Handles milestone tracking, free product add/remove, abuse prevention,
 * and progress bar UI updates for the cart drawer.
 *
 * SETUP: Fill in the variantId values below with the actual Shopify variant IDs
 * for "Free Gift" and "Free Tote Bag" products before going live.
 * Also ensure those products are tagged with the matching tag value.
 */

// ─── Configuration ────────────────────────────────────────────────────────────
const REWARDS_CONFIG = {
  // Thresholds are in paise (Shopify stores money × 100). ₹1000 = 100000.
  milestones: [
    {
      threshold: 100000,
      label: 'Free Shipping',
      type: 'visual', // no product added — just a UI milestone
    },
    {
      threshold: 200000,
      label: 'Free Gift',
      type: 'product',
      variantId: null, // ← REPLACE with actual variant ID from Shopify admin
      productTag: 'free-gift-reward',
    },
    {
      threshold: 400000,
      label: 'Free Tote Bag',
      type: 'product',
      variantId: null, // ← REPLACE with actual variant ID from Shopify admin
      productTag: 'free-tote-reward',
    },
  ],
  maxThreshold: 400000,
  freeRewardProperty: '_free_reward', // line item property key used to identify free items
};

// ─── State ────────────────────────────────────────────────────────────────────
let _isProcessing = false; // prevents concurrent reward validation calls

// ─── Utilities ────────────────────────────────────────────────────────────────

/**
 * Sum line prices for all cart items that are NOT free reward products.
 * @param {Array} items - cart.items array from /cart.js response
 * @returns {number} paid subtotal in paise
 */
function calculateCartTotalExcludingFreeItems(items) {
  return items.reduce((total, item) => {
    const isFree = item.properties && item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true';
    return isFree ? total : total + item.line_price;
  }, 0);
}

/**
 * Check whether a free reward variant is already in the cart.
 * @param {Array} items
 * @param {number} variantId
 * @returns {object|null} the cart line item if found
 */
function findFreeItemInCart(items, variantId) {
  return items.find(
    (item) =>
      item.variant_id === variantId &&
      item.properties &&
      item.properties[REWARDS_CONFIG.freeRewardProperty] === 'true'
  ) || null;
}

/**
 * Add a free reward product to the cart (idempotent — skips if already present).
 * @param {number} variantId
 * @param {Array} currentItems - current cart items (to check idempotency)
 * @returns {Promise<object>} resolved cart state
 */
async function addFreeProduct(variantId, currentItems = []) {
  if (!variantId) {
    console.warn('[CartRewards] variantId is null — update REWARDS_CONFIG before going live.');
    return null;
  }

  if (findFreeItemInCart(currentItems, variantId)) {
    return null; // already in cart, nothing to do
  }

  const properties = {};
  properties[REWARDS_CONFIG.freeRewardProperty] = 'true';

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      properties,
    }),
  });

  if (!response.ok) {
    const err = await response.json();
    console.error('[CartRewards] addFreeProduct failed:', err);
    return null;
  }

  return response.json();
}

/**
 * Remove a free reward product from the cart by variant ID.
 * Uses /cart/change.js to set quantity to 0 on the specific line.
 * @param {number} variantId
 * @param {Array} currentItems
 * @returns {Promise<object|null>}
 */
async function removeFreeProduct(variantId, currentItems = []) {
  if (!variantId) return null;

  const item = findFreeItemInCart(currentItems, variantId);
  if (!item) return null; // not in cart, nothing to remove

  // Use line index (1-based) for precision
  const lineIndex = currentItems.indexOf(item) + 1;

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ line: lineIndex, quantity: 0 }),
  });

  if (!response.ok) {
    const err = await response.json();
    console.error('[CartRewards] removeFreeProduct failed:', err);
    return null;
  }

  return response.json();
}

/**
 * Fetch the latest cart state from Shopify.
 * @returns {Promise<object>} cart object
 */
async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  return response.json();
}

// ─── Core Logic ───────────────────────────────────────────────────────────────

/**
 * Main orchestrator.
 * 1. Calculates paid subtotal (excluding free reward items).
 * 2. Blocks abuse: if paidTotal === 0 but free items exist → remove them all.
 * 3. Adds/removes product-type milestones based on threshold.
 * 4. Updates the progress bar UI.
 *
 * @param {object} cartData - cart object (from /cart.js or the sections response)
 */
async function validateCartRewards(cartData) {
  if (_isProcessing) return;
  _isProcessing = true;

  try {
    // Always fetch fresh cart to avoid stale data from section renders
    const cart = await fetchCart();
    const items = cart.items || [];
    const paidTotal = calculateCartTotalExcludingFreeItems(items);

    const productMilestones = REWARDS_CONFIG.milestones.filter((m) => m.type === 'product');
    let cartChanged = false;

    // Abuse prevention: cart has items but ALL of them are free → purge
    if (items.length > 0 && paidTotal === 0) {
      for (const milestone of productMilestones) {
        const result = await removeFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      }
      updateRewardBarUI(0);
      if (cartChanged) await refreshCartDrawer();
      return;
    }

    // Process milestones from highest to lowest to avoid conflicts
    const sortedMilestones = [...productMilestones].sort((a, b) => b.threshold - a.threshold);

    for (const milestone of sortedMilestones) {
      if (paidTotal >= milestone.threshold) {
        const result = await addFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      } else {
        const result = await removeFreeProduct(milestone.variantId, items);
        if (result) cartChanged = true;
      }
    }

    // Update UI with current paid total (before any cart refresh)
    updateRewardBarUI(paidTotal);

    // If we added/removed products, re-render the drawer to reflect changes
    if (cartChanged) await refreshCartDrawer();

  } catch (err) {
    console.error('[CartRewards] validateCartRewards error:', err);
  } finally {
    _isProcessing = false;
  }
}

// ─── UI Updates ───────────────────────────────────────────────────────────────

/**
 * Update all DOM elements of the reward progress bar.
 * @param {number} paidTotal - paid subtotal in paise
 */
function updateRewardBarUI(paidTotal) {
  const bar = document.getElementById('cart-reward-bar');
  if (!bar) return;

  const fill = document.getElementById('cart-reward-fill');
  const message = document.getElementById('cart-reward-message');
  const amountMarkers = bar.querySelectorAll('.cart-reward__amount-marker');
  const milestoneEls = bar.querySelectorAll('.cart-reward__milestone');
  const { milestones, maxThreshold } = REWARDS_CONFIG;

  // Fill percentage capped at 100
  const pct = Math.min((paidTotal / maxThreshold) * 100, 100);
  if (fill) {
    const previousPct = Number(bar.dataset.fillPct || 0);
    const isIncreasing = pct > previousPct;

    // Keep a tiny visible fill when cart has value so progress is always noticeable.
    const visiblePct = paidTotal > 0 ? Math.max(pct, 2) : 0;

    if (isIncreasing) {
      fill.classList.add('is-increasing');
      window.clearTimeout(fill._increaseAnimTimeout);
      fill._increaseAnimTimeout = window.setTimeout(() => {
        fill.classList.remove('is-increasing');
      }, 850);
    }

    fill.style.width = `${visiblePct}%`;
  }

  // Milestone amount-marker + label states
  milestones.forEach((milestone, index) => {
    const achieved = paidTotal >= milestone.threshold;
    if (amountMarkers[index]) amountMarkers[index].classList.toggle('is-achieved', achieved);
    if (milestoneEls[index]) milestoneEls[index].classList.toggle('is-achieved', achieved);
  });

  // Dynamic message
  if (message) {
    if (paidTotal >= maxThreshold) {
      message.innerHTML = "You've unlocked all rewards! 🎉";
    } else {
      const next = milestones.find((m) => paidTotal < m.threshold);
      if (next) {
        const amountAway = next.threshold - paidTotal;
        // Convert paise to rupees for display
        const rupees = Math.ceil(amountAway / 100);
        message.innerHTML = `You are <strong>₹${rupees}</strong> away from <strong>${next.label}</strong>!`;
      }
    }
  }

  // Sync data attribute so Liquid-rendered value stays in sync
  bar.dataset.cartTotal = paidTotal;
  bar.dataset.fillPct = pct;
}

// ─── Cart Drawer Refresh ───────────────────────────────────────────────────────

/**
 * Re-fetch and re-render the cart drawer section after reward product changes.
 * This mirrors the existing Dawn onCartUpdate pattern in cart.js.
 */
async function refreshCartDrawer() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');

    const selectors = ['cart-drawer-items', '.cart-drawer__footer', '#cart-reward-bar'];
    for (const selector of selectors) {
      const target = document.querySelector(selector);
      const source = html.querySelector(selector);
      if (target && source) target.replaceWith(source);
    }
  } catch (err) {
    console.error('[CartRewards] refreshCartDrawer error:', err);
  }
}

// ─── Event Hooks ──────────────────────────────────────────────────────────────

/**
 * Subscribe to Dawn's pub/sub cart update event.
 * This fires after every quantity change, item remove, or add-to-cart.
 */
function initCartRewardListeners() {
  // Guard: ensure pub/sub is available (it's defined in global.js)
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    // Retry once scripts have loaded
    document.addEventListener('DOMContentLoaded', initCartRewardListeners, { once: true });
    return;
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    // cartData may be on event.cartData (from cart-items updateQuantity publish)
    validateCartRewards(event?.cartData || {});
  });

  // Also run on cart drawer open (catches cases where rewards need syncing on open)
  const cartDrawerEl = document.querySelector('cart-drawer');
  if (cartDrawerEl) {
    const originalOpen = cartDrawerEl.open?.bind(cartDrawerEl);
    if (typeof originalOpen === 'function') {
      cartDrawerEl.open = function (...args) {
        originalOpen(...args);
        validateCartRewards({});
      };
    }
  }
}

// ─── Init ─────────────────────────────────────────────────────────────────────

// Run as soon as the script loads (deferred, so DOM is ready)
initCartRewardListeners();
